'use client';

import * as React from 'react';
import type { CSSProperties, InputHTMLAttributes } from 'react';
import { cx } from '../utils';
import { IS_PROD, devWarn, devWarnValueUndefined } from '../internal/devWarn';
import { JIcon } from '../../icons';

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'style' | 'type'
> {
  className?: string;
  style?: CSSProperties;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { value, onChange, placeholder = 'Search…', className, style, autoFocus, ...rest },
    ref,
  ) {
    /* Mount-only dev-warn; see Input.tsx for the rationale. */
    React.useEffect(() => {
      if (!IS_PROD && value === undefined) devWarnValueUndefined('SearchInput');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Audit #24: warn if no accessible name. We can't detect a paired
       `<label htmlFor>` from inside the component (it lives in adopter
       code), so we accept `aria-label`, `aria-labelledby`, OR an `id`
       (assumes the adopter has a paired label). The placeholder default
       "Search…" is NOT counted as an accessible name.

       Per-instance dedupe (warnedRef) so each mounted SearchInput warns
       at most once. A module-scope dedupe would false-positive in tests
       (all unlabeled instances share the same internal stack frame). */
    const warnedRef = React.useRef(false);
    React.useEffect(() => {
      if (IS_PROD || warnedRef.current) return;
      const ariaLabel = (rest as InputHTMLAttributes<HTMLInputElement>)['aria-label'];
      const ariaLabelledBy = (rest as InputHTMLAttributes<HTMLInputElement>)['aria-labelledby'];
      const id = (rest as InputHTMLAttributes<HTMLInputElement>).id;
      if (ariaLabel || ariaLabelledBy || id) return;
      warnedRef.current = true;
      devWarn(
        '[JKit.SearchInput] no accessible name provided. Pass `aria-label`, `aria-labelledby`, or an `id` paired with a `<label htmlFor>`. Placeholder text is NOT a substitute (screen-reader support is inconsistent and placeholders disappear once typing starts). Audit #24.',
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className={cx('j-search', className)} style={style}>
        <JIcon intent="search" size={14} aria-hidden="true" />
        <input
          {...rest}
          ref={ref}
          type="search"
          value={value ?? ''}
          onChange={onChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
      </div>
    );
  },
);
